import { useMemo } from 'react';
import {
  QBO_INVOICE_PAYMENT_STATUS_LABELS,
  type CustomerQboFinance,
  type QboInvoicePaymentStatus,
} from '@catalyst-psa/shared';

import { Badge } from '../../components/ui/Badge.js';
import { DataState } from '../../components/ui/DataState.js';
import { useCustomerQboFinance } from '../qbo/api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const STATUS_TONES: Record<QboInvoicePaymentStatus, 'green' | 'blue' | 'amber' | 'red'> = {
  paid: 'green',
  partial: 'blue',
  open: 'amber',
  overdue: 'red',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function formatMoney(value: string | null): string {
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? currency.format(n) : value;
}

/**
 * The customer Finance tab: Open-in-QuickBooks link, live QBO open/overdue balances, the
 * last 12 months of QBO invoices with payment status, and PSA-invoice revenue by year.
 */
export function CustomerFinanceTab({ customerId }: { customerId: string }): JSX.Element {
  const financeQuery = useCustomerQboFinance(customerId);
  const finance = financeQuery.data;

  return (
    <DataState
      isLoading={financeQuery.isLoading}
      isError={financeQuery.isError}
      error={financeQuery.error}
      onRetry={() => void financeQuery.refetch()}
      isEmpty={false}
    >
      {finance ? <FinanceContent finance={finance} /> : null}
    </DataState>
  );
}

function FinanceContent({ finance }: { finance: CustomerQboFinance }): JSX.Element {
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <SummaryCard label="QBO Open Balance" value={formatMoney(finance.openBalance)} />
          <SummaryCard
            label="QBO Overdue"
            value={formatMoney(finance.overdueBalance)}
            danger={Number(finance.overdueBalance ?? 0) > 0}
          />
        </div>
        {finance.qboCustomerId ? (
          <a
            href={`https://qbo.intuit.com/app/customerdetail?nameId=${encodeURIComponent(
              finance.qboCustomerId,
            )}`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-brand-600 hover:underline"
          >
            Open in QuickBooks ↗
          </a>
        ) : null}
      </div>

      {!finance.connected && finance.message ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">
          {finance.message}
        </p>
      ) : null}

      {finance.connected ? (
        <div>
          <h3 className="text-sm font-semibold text-slate-800">
            QuickBooks invoices — last 12 months
          </h3>
          {finance.invoices.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">
              No QuickBooks invoices in the last 12 months.
            </p>
          ) : (
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Invoice #</th>
                    <th className="px-3 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Due date</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2 text-right font-medium">Balance</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {finance.invoices.map((invoice) => (
                    <tr key={invoice.qboInvoiceId}>
                      <td className="px-3 py-2">
                        <a
                          href={`https://app.qbo.intuit.com/app/invoice?txnId=${encodeURIComponent(
                            invoice.qboInvoiceId,
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-brand-600 hover:underline"
                        >
                          {invoice.docNumber ?? invoice.qboInvoiceId}
                        </a>
                      </td>
                      <td className="px-3 py-2 text-slate-600">{formatDate(invoice.txnDate)}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDate(invoice.dueDate)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-800">
                        {formatMoney(invoice.total)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">
                        {formatMoney(invoice.balance)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={STATUS_TONES[invoice.status]}>
                          {QBO_INVOICE_PAYMENT_STATUS_LABELS[invoice.status]}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <RevenueByYearChart revenueByYear={finance.revenueByYear} />
    </div>
  );
}

function SummaryCard({
  label,
  value,
  danger = false,
}: {
  label: string;
  value: string;
  danger?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-40 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p
        className={`mt-1 text-lg font-semibold tabular-nums ${
          danger ? 'text-rose-600' : 'text-slate-800'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

/** Simple inline bar chart (no chart dependency) of PSA revenue per calendar year. */
function RevenueByYearChart({
  revenueByYear,
}: {
  revenueByYear: CustomerQboFinance['revenueByYear'];
}): JSX.Element {
  const max = useMemo(
    () => Math.max(...revenueByYear.map((r) => Math.abs(Number(r.total))), 1),
    [revenueByYear],
  );
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-800">Revenue by year (PSA invoices)</h3>
      {revenueByYear.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">No sent or closed PSA invoices yet.</p>
      ) : (
        <div className="mt-3 flex items-end gap-4 overflow-x-auto pb-1">
          {revenueByYear.map((row) => {
            const amount = Number(row.total);
            const height = Math.max(Math.round((Math.abs(amount) / max) * 160), 4);
            return (
              <div key={row.year} className="flex min-w-20 flex-col items-center gap-1">
                <span className="text-xs font-medium tabular-nums text-slate-600">
                  {currency.format(amount)}
                </span>
                <div
                  className={`w-12 rounded-t ${amount < 0 ? 'bg-rose-400' : 'bg-brand-500'}`}
                  style={{ height: `${height}px` }}
                  title={`${row.year}: ${currency.format(amount)}`}
                />
                <span className="text-xs text-slate-500">{row.year}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
